import { memo, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { FileAudioIcon, FileTextIcon, FileVideoIcon } from 'lucide-react';

import { useMountEffect } from '@/shared/hooks/useMountEffect';

import { ICON_BADGE_CLASSES, IMAGE_TAG_SMALL_THUMBNAIL_MAX_HEIGHT } from './FileTag.constants';
import type { DefaultFileTypeIconProps, FileTagProps } from './FileTag.types';

const DefaultFileTypeIcon = ({ fileType }: DefaultFileTypeIconProps) => {
  if (fileType.includes('video')) {
    return <FileVideoIcon className="size-3.5 text-foreground" />;
  }

  if (fileType.includes('audio')) {
    return <FileAudioIcon className="size-3.5 text-foreground" />;
  }

  return <FileTextIcon className="size-3.5 text-foreground" />;
};

/**
 * Renders tags for all file types besides images.
 * Use ImageTag for images.
 */
export const FileTag = memo(
  ({ filename, fileType, url, startAdornment, endAdornment, compact }: FileTagProps) => {
    const nameRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useMountEffect(() => {
      const element = nameRef.current;
      if (element) {
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
      return undefined;
    });

    const linkProps = url
      ? {
          href: url,
          // eslint-disable-next-line i18next/no-literal-string -- anchor target attribute value
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};
    // eslint-disable-next-line i18next/no-literal-string -- element tag names
    const Surface = url ? 'a' : 'div';

    // `||`, not `??`: ReactNode admits booleans, so a caller passing
    // `startAdornment={cond && <Icon/>}` must still get the default badge when
    // `cond` is false. Nullish coalescing would render nothing.
    const startAdornmentNode = startAdornment || (
      <div className={ICON_BADGE_CLASSES} data-testid="file-tag-default-icon">
        <DefaultFileTypeIcon fileType={fileType} />
      </div>
    );

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`relative inline-flex max-w-55.5 ${
                compact ? IMAGE_TAG_SMALL_THUMBNAIL_MAX_HEIGHT : ''
              }`}
            >
              <Surface
                data-testid="file-tag"
                className="flex h-11.5 cursor-pointer items-center gap-2 overflow-hidden rounded-lg border border-border bg-background/90 px-1 transition-colors duration-fast ease-out hover:border-border-strong"
                {...linkProps}
              >
                {startAdornmentNode}

                <div className="flex max-w-25 flex-1 flex-col">
                  <span
                    data-testid="file-name"
                    className="truncate text-sm leading-5"
                    ref={nameRef}
                  >
                    {filename}
                  </span>
                </div>
              </Surface>
              {endAdornment && <div className="absolute -top-2.5 -right-2.5">{endAdornment}</div>}
            </div>
          </TooltipTrigger>
          {isOverflowing && <TooltipContent side="top">{filename}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    );
  },
  (prevProps, nextProps) => {
    // Return true to skip re-render when relevant props are equal
    return (
      prevProps.filename === nextProps.filename &&
      prevProps.fileType === nextProps.fileType &&
      prevProps.url === nextProps.url &&
      prevProps.compact === nextProps.compact
    );
  },
);

FileTag.displayName = 'FileTag';
